import * as React from 'react';
import styled, {css} from 'styled-components';
import {transparentize} from "polished";
import {getImageByPreview} from "api/library";
import {getUsageListItems} from "api/usage-list";
import {CourseObject, editCourse, getCourseByID, getFolders} from "api/course";
import {IconClose, IconLoading, IconNotActiveFolder} from "icons";
import {Container as IconLoadingContainer} from "icons/Loading"
import useClickOutside from "helpers/useClickOutside";
import scrollStyles from "helpers/css/scroll";
import validateResponse from "../helpers/validate-response";
import isError from "helpers/isError";
import {ErrorObject, ID} from "types";
import RoundCheckbox from "./RoundCheckbox";
import compareInLowercase from "helpers/compare-in-lowercase";
import {navigateTo} from "helpers/navigate-to";

export type UsageListItemType = 'course' | 'lesson' | 'task' | 'webinar';

export interface ProgramCardUsageListProps {
  type: 'cards' | 'folders';
  title?: string | React.ReactElement;
  itemType?: UsageListItemType;
  itemId: ID;
  onIconCloseClick: () => void;
}

const Container = styled.div`
  width: 600px;
  border-radius:5px;
  background-color: #232323;
  z-index: 100;
  box-shadow: 0 10px 40px rgba(24, 24, 24, 0.7);
  transition: visibility .22s, opacity .22s;
`

const ContainerTitle = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  padding: 20px 30px;
  cursor: default;
  
  svg {
    width: 14px;
    height: 14px;
    margin: 0;
    cursor: pointer;
    path {
      fill: white;
      fill-opacity: .35;
      transition: fill-opacity .22s;
    }
    &:hover path{
      fill-opacity: .85;
    }
  }
`

const List = styled.div`
  padding-bottom: 10px;
  max-height: 454px;
  overflow-y: auto;
  
  ${scrollStyles()}
`

const Card = styled.a`
  display: flex;
  align-items: center;
  margin-left: 30px;
  padding: 5px;
  cursor: pointer;
  transition: background-color .22s;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;

  &:hover {
    background-color: #2E2E2E;
  }
`

const Preview = styled.div`
  min-height: 50px;
  max-height: 50px;
  min-width: 90px;
  max-width: 90px;
  left: 5px;
  top: 5px;
  border-radius: 5px;
  overflow: hidden;
  margin-right: 15px;
  
  img {
    width: 100%;
    object-fit: cover;
  }
`

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.01em;
`

const NoDataBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  color: ${({theme}) => transparentize(.8, theme.variables.color)};
  font-size: 18px;
  
  ${IconLoadingContainer} {
    margin: 0;
  }
`
const FoldersContainer = styled.div`
  display: grid;
  gap: 5px;
  padding-left: 20px;
  padding-right: 10px;
`

const FolderLabel = styled.div`
  margin-left: 10px;
  margin-right: auto;
  color: ${({theme}) => theme.variables.color};
`

const FolderItem = styled.label<{isActive: boolean}>`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 10px;
  font-size: 15px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.01em;
  color: ${({isActive, theme}) => isActive 
    ? theme.variables.color
    : transparentize(.6, theme.variables.color)
  };
  
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  transition-property: background-color, border-color;
  transition-duration: .22s;

  &:hover {
    background-color: ${({theme}) => transparentize(.98, theme.variables.color)};
  }
  
  ${({isActive}) => {
    if (isActive) {
      return css`
        border-color: ${({theme}) => transparentize(.90, theme.variables.color)};
        background-color: ${({theme}) => transparentize(.95, theme.variables.color)};
      `
    }
  }}
`


export default function ProgramCardUsageList(
  props: ProgramCardUsageListProps
): React.ReactElement {
  const wrapperRef = React.createRef<HTMLDivElement>();

  const [isLoading, setLoading] = React.useState(true);
  const [items, setItems] = React.useState<any[]>([]);
  const [course, setCourse] = React.useState<CourseObject>();
  const [allFolders, setAllFolders] = React.useState<string[]>([])

  /**
   * The handle click outside of the wrapper.
   */
  useClickOutside(wrapperRef, props.onIconCloseClick);

  /**
   * fetch and set data
   */
  React.useEffect(() => {
    setLoading(true);

    async function fetchUsageListItems() {
      return await getUsageListItems(props.itemId)
    }

    async function fetchCourse() {
      return await getCourseByID(props.itemId)
    }

    async function fetchAllFolders() {
      return await getFolders()
    }

    /**
     * the function set course folders to component state
     */
    function setFoldersToItems(resp: CourseObject | ErrorObject | undefined) {
      validateResponse(resp);

      if (resp && !isError(resp)) {
        const folders = resp.folders;

        folders && setItems(folders);
        setCourse(resp);
      }
    }

    /**
     * the function set all teacher folders to component state
     */
    function setFetchedAllFolders(
      resp: ErrorObject | string[] | undefined
    ) {
      Array.isArray(resp) && setAllFolders(resp)
    }

    if (props.type === 'cards') {
      fetchUsageListItems()
        .then(setItems)
        .then(() => setLoading(false));
    }

    if (props.type === 'folders') {
      fetchCourse()
        .then(setFoldersToItems)
        .then(fetchAllFolders)
        .then(setFetchedAllFolders)
        .then(() => setLoading(false));
    }

  }, [props.itemId])

  /**
   * the function deletes or adds a folder to the course
   * and updates the state of the component
   */
  function onFolderClickHandler(folder: string, isActive: boolean) {
    if (course) {
      let courseFolders = course?.folders || [];

      isActive
        ? courseFolders = courseFolders.filter(item => item !== folder)
        : courseFolders.push(folder);

      const updatedCourse: CourseObject = {...course, folders: courseFolders};

      editCourse(updatedCourse)
        .then(resp => {
          validateResponse(resp);

          if (resp && !isError(resp)) {
            setCourse(resp);
            setItems(courseFolders)
          }
        })
    }
  }

  function onCardClickHandler(e: React.MouseEvent, course: CourseObject) {
    e.preventDefault();
    navigateTo('courses.course.edit', {id: course.id})
  }

  /**
   * the function render content by props.type
   */
  function renderContent(): JSX.Element | JSX.Element[] {
    if (props.type === 'cards') {
      return renderCards();
    }
    if (props.type === 'folders') {
      return renderFolders();
    }

    return [];
  }

  /**
   * the function render cards if props.type === 'folders'
   */
  function renderFolders() {
    if (!isLoading && allFolders.length) {
      const sorted = allFolders.sort(compareInLowercase);

      return (
        <FoldersContainer>
          {sorted.map((folder, ind) => {
            const isActive = items.includes(folder);

            return (
              <FolderItem
                key={ind+folder}
                isActive={isActive}
                onClick={() => onFolderClickHandler(folder, isActive)}
              >
                <IconNotActiveFolder />
                <FolderLabel>{folder}</FolderLabel>
                <RoundCheckbox checked={isActive} />
              </FolderItem>
            )
          })}
        </FoldersContainer>
      )
    }

    return renderNoData()
  }

  /**
   * the function render cards if props.type === 'cards'
   */
  function renderCards(): JSX.Element | JSX.Element[] {
    if (items.length) {
      return items.map((card: CourseObject, ind: number) => (
          <Card
            key={ind + card.id}
            href={`/courses/${card.id}/edit`}
            onClick={(e) => onCardClickHandler(e, card)}
          >
            <Preview>
              <img src={getImageByPreview(card.preview)} alt='card icon'/>
            </Preview>
            <CardTitle>{card.title}</CardTitle>
          </Card>
        ))
    }

    return renderNoData()
  }

  /**
   * the function render no data component with loader or text
   */
  function renderNoData() {
    const content = isLoading
      ? <IconLoading />
      : 'Ничего не найдено...';

    return (
      <NoDataBox>
        {content}
      </NoDataBox>
    )
  }

  return (
    <Container ref={wrapperRef}>
      <ContainerTitle>
        {props.title || 'Используется в ...'}
        <div onClick={props.onIconCloseClick}>
          <IconClose />
        </div>
      </ContainerTitle>
      <List>
        {renderContent()}
      </List>
    </Container>
  );
}
